<script setup lang="ts">
import {
  computed,
  onUnmounted,
  ref,
  toRef,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormResult,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import VMerchantLogoPreview from '@modules/merchant-settings/components/VMerchantLogoPreview/VMerchantLogoPreview.vue';
import { useMerchantLogoPreview } from '@modules/merchant-settings/composables';
import { ADMIN_MERCHANT_SETTINGS_LIST_ROUTE_NAME } from '@modules/merchant-settings/constants';
import { useMerchantStore } from '@modules/merchant/store';
import { fileToBase64 } from '@utils/file';
import { assert, isNumber, isUndefined } from '@utils/guards';

import type { IMerchantSettingsEditForm, IMerchantSettingsEditPayload } from '../../types';
import { TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT } from '../../constants';
import { useEditMerchantSettingsMutation } from '../../mutations';
import { useGetMerchantSettingsQuery } from '../../queries';

const merchantStore = useMerchantStore();
const { updateMerchantPreview, updateMerchant } = merchantStore;
const route = useRoute();
const { getTranslation } = useI18n();
const currentMerchant = toRef(merchantStore, 'merchant');
const switchPreviewValue = ref(false);
const logoValue = ref<File>();

const id = computed<number | undefined>(() => (route.params.id ? Number(route.params.id) : undefined));

const isSwitchDisabled = computed(() => currentMerchant.value?.id !== id.value);

const { data: merchant, isLoading: isGetLoading } = useGetMerchantSettingsQuery(id);

const {
  mutateAsync: editMerchant,
  data: editResponse,
  isLoading: isEditLoading,
  errors,
  clearError,
} = useEditMerchantSettingsMutation();

const {
  merchantLogoUrl,
  merchantName,
  onPreview,
  onPreviewHide,
} = useMerchantLogoPreview();

const formSchema = computed<FormSchema<IMerchantSettingsEditForm>>(() => ({
  merchant_name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'MERCHANT_NAME_LABEL').value,
    value: merchant.value?.merchant_name,
    required: true,
    readonly: isEditLoading.value,
  },
  header_color: {
    type: EFormInputTypes.COLOR,
    label: getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'HEADER_COLOR_LABEL').value,
    value: merchant.value?.header_color || '',
    readonly: isEditLoading.value,
  },
  buttons_color: {
    type: EFormInputTypes.COLOR,
    label: getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'BUTTONS_COLOR_LABEL').value,
    value: merchant.value?.buttons_color || '',
    readonly: isEditLoading.value,
  },
  logo: {
    type: EFormInputTypes.SINGLE_FILE,
    label: getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'LOGO_LABEL').value,
    disabled: isEditLoading.value,
    accept: '.png, .jpg, .jpeg, .svg',
    format: 'png, jpg, svg',
    maxSizeBytes: 2_097_152, // 2 MB
    iconType: 'image',
    value: logoValue.value,
  },
  isPreviewEnabled: {
    type: EFormInputTypes.SWITCH,
    label: getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'PREVIEW').value,
    trueLabel: getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'PREVIEW_ENABLED').value,
    falseLabel: getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'PREVIEW_DISABLED').value,
    disabled: isEditLoading.value || isSwitchDisabled.value,
    value: switchPreviewValue.value,
    hideErrors: true,
  },
  previewDescription: {
    type: EFormInputTypes.HIDDEN,
    label: getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'PREVIEW_DESCRIPTION').value,
    isHidden: !isSwitchDisabled.value,
  },
}));

function onChange<
  T extends IMerchantSettingsEditForm,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
  result: FormResult<IMerchantSettingsEditForm>,
): void {
  switchPreviewValue.value = !!result.values.isPreviewEnabled;
  logoValue.value = result.values.logo;

  if (!merchant.value || isSwitchDisabled.value) {
    return;
  }

  if (!result.values.isPreviewEnabled) {
    if (key === 'isPreviewEnabled') {
      updateMerchantPreview({ ...merchant.value });
    }
    return;
  }

  if (logoValue.value) {
    void fileToBase64(logoValue.value).then((content) => {
      if (merchant.value) {
        updateMerchantPreview({
          ...merchant.value,
          merchant_name: result.values.merchant_name,
          header_color: result.values.header_color,
          buttons_color: result.values.buttons_color,
          logo_url: content,
        });
      }
    });
  } else {
    updateMerchantPreview({
      ...merchant.value,
      merchant_name: result.values.merchant_name,
      header_color: result.values.header_color,
      buttons_color: result.values.buttons_color,
    });
  }
}

async function onSubmit({ isPreviewEnabled, previewDescription, ...values }: IMerchantSettingsEditForm): Promise<void> {
  assert(isNumber(id.value));

  const formData = new FormData();

  for (const [name, value] of Object.entries(values)) {
    if (!isUndefined(value)) {
      formData.append(name, value || '');
    }
  }

  const payload: IMerchantSettingsEditPayload = {
    id: id.value,
    merchant: formData,
  };

  try {
    await editMerchant(payload);

    if (!isSwitchDisabled.value && editResponse.value?.merchant) {
      updateMerchant(editResponse.value.merchant);
      logoValue.value = undefined;
    }
  } catch {}
}

watch(isSwitchDisabled, (newValue) => {
  if (newValue) {
    switchPreviewValue.value = false;
  }
});

onUnmounted(() => {
  updateMerchantPreview(undefined);
});
</script>

<template>
  <VCard v-data-qa-auto-id="'MerchantSettingsEditPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'HEADER', { merchant_name: merchant?.merchant_name })"
  >
    <VLoader
      :loading="isGetLoading"
      min-height="630px"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'SUBMIT')"
        :custom-errors="errors"
        :is-loading="isEditLoading"
        @interacted="clearError"
        @change="onChange"
        @submit="onSubmit"
      >
        <template #secondaryBtn>
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'SHOW_CURRENT_LOGO')"
            icon="pi-eye"
            :disabled="!merchant?.logo_url"
            @click="onPreview(merchant?.logo_url, merchant?.merchant_name)"
          />
          <VBackButton
            :label="$getTranslation(TRANSLATION_GROUP_MERCHANT_SETTINGS_EDIT, 'BACK')"
            :to="{ name: ADMIN_MERCHANT_SETTINGS_LIST_ROUTE_NAME }"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>

  <VMerchantLogoPreview
    :logo-url="merchantLogoUrl"
    :title="merchantName"
    @hide="onPreviewHide"
  />
</template>
